import { COACH_MODULES } from '@/components/coach';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@headlessui/react';
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeviceId } from '../../hooks/useDeviceId';
import { cn } from '../../lib/utils';
import { constructLanguageUrl } from '../../router';
import api from '../../utils/ky';

export default function CoachCategories() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { lang } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isNameDialogOpen, setNameDialogOpen] = useState(false);
  const deviceId = useDeviceId();

  // Get user info from localStorage first
  const storedName = localStorage.getItem('user_name');
  const storedCompany = localStorage.getItem('user_company');

  // Query for existing user if deviceId exists and no stored name
  const {
    data: userData,
    isLoading: isLoadingUser,
    isError,
  } = useQuery({
    queryKey: ['user', deviceId],
    queryFn: async () => {
      try {
        // Try to get user from API
        const response = await api
          .get('api/coach/users', {
            searchParams: { deviceId },
          })
          .json<{ userId: string, name: string; company: string }>();

        if (!response || !response.userId) {
          return null;
        }
        // Store in localStorage
        localStorage.setItem('user_name', response.name);
        localStorage.setItem('user_company', response.company);

        return response;
      } catch (error: any) {
        throw error;
      }
    },
    enabled: !!deviceId,
    retry: false, // Don't retry on 404
  });

  const visibleModules = useMemo(() => {
    return COACH_MODULES.filter((module) => {
      // If it's English language, show all modules
      if (lang === 'en') return true;
      // For other languages, only show modules that aren't English-only
      return !module.isEnglishOnly;
    });
  }, [lang]);

  useEffect(() => {
    if (!searchParams.has('code')) {
      const pathToMain = constructLanguageUrl(lang, '/coach');
      navigate(pathToMain);
    }
  }, [searchParams]);

  const navigateToChat = (
    userName: string,
    companyName: string,
    categoryId: string,
  ) => {
    console.log('navigateToChat', userName, companyName, categoryId);
    const pathToChat = constructLanguageUrl(
      lang,
      '/coach/chat',
      `?${searchParams.toString()}`,
    );
    navigate(pathToChat, {
      state: {
        userName,
        category: categoryId,
        companyName,
      },
    });
  };

  const { mutate: createUser, isPending: isCreatingUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
      code: string;
    }) =>
      api
        .post('api/coach/users', {
          json: data,
        })
        .json<{
          userId: string;
          deviceId: string;
          companyName: string;
        }>(),
    onSuccess: (response, variables) => {
      // Store user info in localStorage
      localStorage.setItem('user_name', variables.name);
      localStorage.setItem('user_company', response.companyName);

      posthog.capture('user_created', {
        deviceId: variables.deviceId,
        code: variables.code,
      });

      navigateToChat(variables.name, response.companyName, selectedCategory);
    },
    onError: () => {
      toast.error('Failed to create user. Please try again.');
    },
  });

  const handleNameSubmit = (name: string) => {
    setNameDialogOpen(false);
    if (deviceId) {
      createUser({
        name,
        deviceId,
        function: 'other',
        code: searchParams.get('code') || '',
      });
    }
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category.id);
    // If user exists, navigate directly to chat
    if (userData?.name) {
      navigateToChat(userData.name, userData.company, category.id);
    } else {
      setNameDialogOpen(true);
    }
  };

  if (!deviceId || isLoadingUser || isCreatingUser) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="px-6 py-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('coach.categories.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            {t('coach.categories.subtitle')}
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {visibleModules.map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={cn(
                'relative group bg-white p-6 rounded-xl shadow-sm transition-all duration-200',
                !category.locked && 'hover:shadow-md cursor-pointer',
                category.locked && 'opacity-75',
              )}
              onClick={() => !category.locked && handleCategorySelect(category)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-4xl mb-4 block">{category.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t(`coach.modules.${category.id}.title`)}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t(`coach.modules.${category.id}.description`)}
                  </p>
                </div>

                {category.locked ? (
                  <div className="text-gray-400">
                    <LockClosedIcon className="h-5 w-5" />
                  </div>
                ) : (
                  <div className="text-primary">
                    <CheckCircleIcon className="h-5 w-5" />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <NameDialog
        isOpen={isNameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        onSubmit={handleNameSubmit}
      />
    </div>
  );
}

export const NameDialog = ({ isOpen, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6">
          <Dialog.Title className="text-lg font-medium mb-4">
            {t('coach.namedialog.title')}
          </Dialog.Title>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('coach.namedialog.placeholder')}
            className="mb-4"
          />
          <Button
            onClick={() => onSubmit(name)}
            disabled={!name.trim()}
            className="w-full"
          >
            {t('coach.namedialog.ctalabel')}
          </Button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
